import { GzCheckboxWebComponent, GzEnumListWebComponent } from '../../../index';
import { EnumListItem, GroupType, SelectedOption } from '../../gzEnumList/types';
import { COMPANIES_LIST_ID, CompanyListEvents } from '../types';
import { html, PropertyValues, TemplateResult } from 'lit';
import { query, property } from 'lit/decorators.js';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { debounce } from '../../../utils/event.utils';
import { ElementClass, elementClassListAdd, elementClassListRemove } from '../../../utils/htmlElement.utils';
import { styles } from '../view/styles';
import { CompanySpecialValues } from '@nirvana/shared/dtos/types/shared/filters';
import { translateFn } from '../../../utils/translator.helper';

export class GzCompanyListWebComponent extends GzEnumListWebComponent {
  private _isLoading = false;

  @query('#company-loader')
  private loader;

  @query('#allDirectManagement')
  private readonly allDirectManagement!: HTMLElement;

  @property({ type: String, attribute: true, reflect: true })
  allDirectManagementLabel = '';

  @property({ type: Boolean, reflect: true })
  showOnlySelectAll = false;

  @property({ type: Boolean, reflect: true })
  showOnlySelectAllDirectManagement = false;

  @property({ type: Boolean, reflect: true })
  showNone = false;

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);
    if (changedProperties.has('filterOptions') && this.listIsFiltered) {
      super.handleOptionsSearch(
        new CustomEvent('search', {
          detail: {
            sourceEvent: this.searchInput.value,
          },
        })
      );
    }
  }

  protected shouldUpdate(changedProperties): boolean {
    return !(this.groupType === GroupType.DEFAULT && changedProperties.get('listIsFiltered') === false);
  }

  public getNumberOfVisibleCompanies() {
    return Array.from(this.optionsList).filter((option: Element) => !option.classList.contains(ElementClass.HIDDEN))
      .length;
  }

  protected updateFilteredCheckboxesBySearchResult(searchResult: EnumListItem[]) {
    if (this.filterOptions.length !== this.optionsList.length) {
      throw Error('Unable to sync company options in selector!');
    }

    super.updateFilteredCheckboxesBySearchResult(searchResult);
  }

  public getCheckedOptions(): SelectedOption[] {
    const checkedValues: SelectedOption[] = [];

    this.checkboxesList.forEach((checkbox: GzCheckboxWebComponent) => {
      if (checkbox.checked) {
        const { label, value } = checkbox;
        checkedValues.push({ label, value: value });
      }
    });

    return checkedValues;
  }

  public getSelectedValuesFromOptionsList(): SelectedOption[] {
    return this.getCheckedOptions();
  }

  public getSelectedValuesFromFilterOptions(): SelectedOption[] {
    return super.getSelectedValuesFromOptionsList();
  }

  protected renderSearchInput() {
    return html` <gz-input-search
      placeholder="${this.searchPlaceholderString}"
      inputWidth="medium"
      .disabled="${isEmpty(this.filterOptions)}"
      @change="${debounce(this.handleOptionsSearch, 700)}"
    ></gz-input-search>`;
  }

  protected renderListOptions() {
    return html` <div id="${COMPANIES_LIST_ID}" class="list--options" @scroll="${this.loadMoreCompanies}">
      <div id="company-loader" class="loader-hidden">${this.renderLoadingTemplate()}</div>
      ${this.renderSelectAllItemsCheckbox()} ${this.renderFilterOptionsList()} ${this.renderNoResultsTemplate()}
    </div>`;
  }

  private renderLoadingTemplate() {
    return html` <div class="spinner">
      <gz-progress-spinner spinnerwidth="medium"></gz-progress-spinner>
    </div>`;
  }

  protected renderFilterOptionsList() {
    if (this.showNone && !isEmpty(this.filterOptions) && !this.filterOptions.some(item => item.value === 'none')) {
      this.filterOptions.unshift({
        label: translateFn('None'),
        value: 'none',
        selected: false,
      });
    }

    return super.renderFilterOptionsList();
  }

  protected renderSelectAllItemsCheckbox(): TemplateResult | undefined {
    if (!this.canSelectAll) {
      return;
    }

    if (this.showOnlySelectAll && this.showOnlySelectAllDirectManagement) {
      throw new Error('Cannot enable both properties at the same time.');
    }

    const selectAllCheckboxHtml = html`
      <div id="allCheckboxesSelect" class="option option-all">
        <gz-checkbox
          class="is-filterable full-checkbox"
          value="${CompanySpecialValues.ALL}"
          label="${this.allItemsLabel}"
          ?checked="${false}"
          @change="${this.handleExclusiveSelection}"
        ></gz-checkbox>
      </div>
    `;

    const selectAllDirectManagementCheckboxHtml = html`
      <div id="allDirectManagement" class="option option-all">
        <gz-checkbox
          class="is-filterable full-checkbox"
          value="${CompanySpecialValues.ALL_DIRECT_MANAGEMENT}"
          label="${this.allDirectManagementLabel}"
          ?checked="${false}"
          @change="${this.handleExclusiveSelection}"
        ></gz-checkbox>
      </div>
    `;

    if (this.showOnlySelectAll) {
      return selectAllCheckboxHtml;
    }

    if (this.showOnlySelectAllDirectManagement) {
      return selectAllDirectManagementCheckboxHtml;
    }

    return html` ${selectAllCheckboxHtml} ${selectAllDirectManagementCheckboxHtml} `;
  }

  protected loadMoreCompanies(e: CustomEvent) {
    this.dispatchEvent(
      new CustomEvent(CompanyListEvents.LOAD_MORE_COMPANIES, {
        detail: {
          sourceEvent: e,
        },
        bubbles: true,
        composed: true,
      })
    );
    return;
  }

  protected matchCheckbox(checkbox: GzCheckboxWebComponent, selectedOption: SelectedOption) {
    return selectedOption.value === checkbox.value;
  }

  protected handleOptionsSearch(e: CustomEvent) {
    const searchString = e.detail.sourceEvent;

    if (searchString === '') {
      this.dispatchEvent(
        new CustomEvent(CompanyListEvents.SEARCH_CLEAR, {
          detail: {
            sourceEvent: e,
          },
          bubbles: true,
          composed: true,
        })
      );
      return;
    }

    this.dispatchEvent(
      new CustomEvent(CompanyListEvents.SEARCH_COMPANY, {
        detail: {
          searchString,
          sourceEvent: e,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  protected hasSearchInput(): boolean {
    return true;
  }

  public continueHandleOptionsSearch(sourceEvent: CustomEvent) {
    super.handleOptionsSearch(sourceEvent);
  }

  protected handleAllValueSearch(search: string) {
    super.handleAllValueSearch(search);

    if (!this.allDirectManagement) {
      return;
    }

    search
      ? elementClassListAdd(this.allDirectManagement, ElementClass.HIDDEN)
      : elementClassListRemove(this.allDirectManagement, ElementClass.HIDDEN);
  }

  protected getFilteredResults(searchString: string) {
    const isRegex = searchString.charAt(0) === '*';

    return this.filterOptions.filter((option: EnumListItem) => {
      const filterableLabel = option.label.toLowerCase();
      return isRegex ? filterableLabel.includes(searchString.slice(1)) : filterableLabel.startsWith(searchString);
    });
  }

  get isLoading(): boolean {
    return this._isLoading;
  }

  set isLoading(value: boolean) {
    if (value && !this._isLoading) {
      this.loader.classList.remove('loader-hidden');
    }

    if (!value && this._isLoading) {
      this.loader.classList.add('loader-hidden');
    }

    this._isLoading = value;
  }

  public handleCheckboxChange(option: EnumListItem, e: CustomEvent) {
    this.unselectSpecialOptions();
    super.handleCheckboxChange(option, e);
  }

  private handleExclusiveSelection(event) {
    const value = event.detail.value;
    this.selectOnlyValue(value);
    this.emitFilterOptionToggledEvent();
  }

  private selectOnlyValue(value: string) {
    this.checkboxesList.forEach((checkbox: GzCheckboxWebComponent) => {
      // Update checkboxes
      checkbox.checked = checkbox.value === value;
    });

    // Update selected filterOptions
    this.filterOptions = this.filterOptions.map((item: EnumListItem) => {
      if (item.value === value) {
        return { ...item, selected: true };
      }

      return { ...item, selected: false };
    });
  }

  public unselectSpecialOptions() {
    this.checkboxesList
      .filter((checkbox: GzCheckboxWebComponent) =>
        Object.values(CompanySpecialValues).includes(checkbox.value as CompanySpecialValues)
      )
      .forEach((checkbox: GzCheckboxWebComponent) => {
        checkbox.checked = false;
      });
  }
}
